import { useState } from 'react';
import { Link } from 'react-router-dom';

const STATS = [
  { value: '500+', label: 'Happy Clients' },
  { value: '8+', label: 'Years Experience' },
  { value: '2', label: 'Counties Served' },
  { value: '100%', label: 'Satisfaction Rate' },
];

const SERVICES = [
  {
    icon: 'ri-home-heart-line',
    title: 'Residential Cleaning',
    desc: 'Thorough, reliable home cleaning tailored to your schedule and preferences.',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Commercial Cleaning',
    desc: 'Keep your workplace spotless and professional with our commercial cleaning programs.',
  },
  {
    icon: 'ri-sparkling-2-line',
    title: 'Deep Cleaning',
    desc: 'A comprehensive top-to-bottom deep clean for those extra-special occasions.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Move In / Move Out',
    desc: 'Stress-free transitions with our complete move-in and move-out cleaning solutions.',
  },
];

const TESTIMONIALS = [
  {
    name: 'Maria Gonzalez',
    city: 'Port St. Lucie',
    stars: 5,
    text: 'A&S Prestige Cleaning Service is simply the best in the area! My home has never looked so clean and fresh. Their team is professional, thorough, and always on time.',
    avatar: 'https://public.readdy.ai/ai/img_res/a686ef3a097bf7ac7c909929f5ba104c.jpg',
  },
  {
    name: 'James Walker',
    city: 'Stuart',
    stars: 5,
    text: 'We use A&S for our office in Stuart and couldn\'t be happier. The commercial cleaning team is attentive to every detail and very respectful of our workspace.',
    avatar: 'https://readdy.ai/api/search-image?query=professional%20headshot%20smiling%20middle%20aged%20man%20business%20casual%20warm%20natural%20lighting&width=80&height=80&seq=t2&orientation=squarish',
  },
  {
    name: 'Sandra Mitchell',
    city: 'Jensen Beach',
    stars: 5,
    text: 'They did our move-out cleaning and got us our full deposit back! Super detail-oriented and very friendly. Will absolutely use them again for our new home.',
    avatar: 'https://readdy.ai/api/search-image?query=smiling%20woman%20professional%20headshot%20blonde%20natural%20lighting%20clean%20background&width=80&height=80&seq=t3&orientation=squarish',
  },
];

const FAQS = [
  {
    q: 'What areas do you serve?',
    a: 'We proudly serve all cities in Port St. Lucie (St. Lucie County) and Martin County, Florida — including Port St. Lucie, Fort Pierce, Stuart, Jensen Beach, Palm City, Hobe Sound, Tradition, and more.',
  },
  {
    q: 'Do I need to provide cleaning supplies?',
    a: 'Yes our team will provide you with list of items they are looking for. We purchase them for you without any upcharge or you can pick them up youself.',
  },
  {
    q: 'How do I schedule a cleaning?',
    a: 'Simply fill out our request form on the Contact page, call us, or email us. We\'ll get back to you within one business day to confirm your appointment.',
  },
  {
    q: 'Are your cleaners insured and background-checked?',
    a: 'Absolutely. All of our cleaning professionals are fully insured, bonded, and have passed thorough background checks for your peace of mind.',
  },
  {
    q: 'Do you offer recurring cleaning plans?',
    a: 'Yes! We offer weekly, bi-weekly, and monthly recurring cleaning plans at discounted rates for both residential and commercial customers.',
  },
];

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'CleaningService',
  name: 'A&S Prestige Cleaning Service',
  url: SITE_URL,
  telephone: '+15618461326',
  email: 'asprestigecleaningservice62@gmail.com',
  description: 'Professional residential and commercial cleaning services in Port St. Lucie and Martin County, FL. Insured, background-checked, satisfaction guaranteed.',
  image: `${SITE_URL}/og-image.jpg`,
  priceRange: '$$',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Port St. Lucie',
    addressRegion: 'FL',
    postalCode: '34952',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: '27.2937',
    longitude: '-80.3503',
  },
  areaServed: [
    { '@type': 'City', name: 'Port St. Lucie' },
    { '@type': 'City', name: 'Stuart' },
    { '@type': 'City', name: 'Fort Pierce' },
    { '@type': 'City', name: 'Jensen Beach' },
    { '@type': 'City', name: 'Palm City' },
    { '@type': 'AdministrativeArea', name: 'St. Lucie County' },
    { '@type': 'AdministrativeArea', name: 'Martin County' },
  ],
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Saturday'],
      opens: '09:00',
      closes: '16:00',
    },
  ],
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Cleaning Services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Residential Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Commercial Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Deep Cleaning' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Move In / Move Out Cleaning' } },
    ],
  },
};

const WEBSITE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'A&S Prestige Cleaning Service',
  url: SITE_URL,
  description: 'Professional cleaning services in Port St. Lucie and Martin County, FL.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${SITE_URL}/contact`,
    'query-input': 'required name=search_term_string',
  },
};

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <main>
      <title>Port St. Lucie Cleaning Service | A&S Cleaning Service | Martin County FL</title>
      <meta name="description" content="A&S Cleaning Service offers professional residential and commercial cleaning in Port St. Lucie, Stuart, Fort Pierce, Jensen Beach, Palm City and all of Martin County, FL. Get a free quote today!" />
      <meta name="keywords" content="cleaning service port st lucie, commercial cleaning martin county, residential cleaning florida, house cleaning stuart fl, deep cleaning jensen beach" />
      <link rel="canonical" href={`${SITE_URL}/`} />
      <meta property="og:title" content="Port St. Lucie Cleaning Service | A&S Cleaning Service" />
      <meta property="og:description" content="Professional residential and commercial cleaning in Port St. Lucie and Martin County, FL. Insured, background-checked, satisfaction guaranteed." />
      <meta property="og:url" content={`${SITE_URL}/`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(LOCAL_BUSINESS_SCHEMA) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(WEBSITE_SCHEMA) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=modern%20bright%20clean%20living%20room%20after%20professional%20cleaning%20sparkling%20surfaces%20fresh%20flowers%20sunlight%20streaming%20through%20windows%20luxury%20interior%20design%20pristine%20spotless%20home%20beautiful%20staged%20interior%20south%20florida%20style&width=1440&height=900&seq=hero1&orientation=landscape"
          alt="Professional cleaning service result"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/60" />
        <div className="relative z-10 w-full text-center px-6 lg:px-12 flex flex-col items-center">
          <span className="inline-block bg-emerald-600/90 text-white text-xs font-bold tracking-widest uppercase px-5 py-2 rounded-full mb-6">
            Port St. Lucie &amp; Martin County
          </span>
          <h1 className="text-5xl lg:text-7xl font-extrabold text-white leading-tight max-w-4xl mb-6" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Spotless Spaces.<br />
            <span className="text-emerald-400">Guaranteed.</span>
          </h1>
          <p className="text-2xl text-gray-200 max-w-2xl mb-10 leading-relaxed">
            A&amp;S Prestige Cleaning Service delivers professional residential and commercial cleaning you can count on — serving all of Port St. Lucie and Martin County FL.
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Link
              to="/contact"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-10 py-4 rounded-full transition-colors text-base whitespace-nowrap cursor-pointer"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/services"
              className="border-2 border-white text-white hover:bg-white hover:text-gray-900 font-bold px-10 py-4 rounded-full transition-colors text-base whitespace-nowrap cursor-pointer"
            >
              Our Services
            </Link>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full fill-white" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white py-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-center">
              <p className="text-4xl font-extrabold text-emerald-600" style={{ fontFamily: "'Montserrat', sans-serif" }}>{s.value}</p>
              <p className="text-sm text-gray-500 mt-1 font-medium">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-emerald-600 font-bold text-sm tracking-widest uppercase">Why A&amp;S?</span>
            <h2 className="text-4xl font-extrabold text-gray-900 mt-3 mb-6 leading-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              The Cleaning Service<br />Port St. Lucie Trusts
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              For years, A&amp;S Prestige Cleaning Service has been the go-to choice for homeowners and businesses across Port St. Lucie and Martin County. We bring consistency, care, and exceptional results to every job.
            </p>
            <ul className="space-y-5">
              {[
                { icon: 'ri-shield-check-line', title: 'Insured &amp; Background-Checked', desc: 'Every team member is fully vetted and insured for your peace of mind.' },
                { icon: 'ri-award-line', title: 'Satisfaction Guaranteed', desc: 'Not happy? We\'ll come back and make it right — no questions asked.' },
                { icon: 'ri-leaf-line', title: 'Eco-Friendly Products', desc: 'We use safe, non-toxic cleaning solutions that are kind to your family and the environment.' },
                { icon: 'ri-calendar-check-line', title: 'Flexible Scheduling', desc: 'One-time or recurring — we work around your schedule, not the other way around.' },
              ].map((item) => (
                <li key={item.title} className="flex items-start gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-emerald-100 rounded-xl shrink-0">
                    <i className={`${item.icon} text-emerald-600 text-xl`} />
                  </div>
                  <div>
                    <strong className="text-gray-900 font-semibold" dangerouslySetInnerHTML={{ __html: item.title }} />
                    <p className="text-gray-500 text-sm mt-0.5">{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=professional%20cleaning%20team%20two%20smiling%20women%20in%20green%20uniforms%20with%20cleaning%20supplies%20standing%20in%20bright%20modern%20south%20florida%20home%20spotless%20kitchen%20background%20warm%20lighting%20high%20quality&width=700&height=700&seq=why1&orientation=squarish"
              alt="A&S Cleaning Service professional team"
              className="w-full h-[520px] object-cover object-top rounded-2xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-emerald-600 text-white rounded-2xl px-8 py-5">
              <p className="text-3xl font-extrabold" style={{ fontFamily: "'Montserrat', sans-serif" }}>8+</p>
              <p className="text-sm font-medium opacity-90">Years Serving<br />Port St. Lucie &amp; Martin County</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <span className="text-emerald-600 font-bold text-sm tracking-widest uppercase">What We Do</span>
            <h2 className="text-4xl font-extrabold text-gray-900 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Cleaning Services We Provide
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title} className="bg-gray-50 rounded-2xl p-7 hover:bg-emerald-50 transition-colors group">
                <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 group-hover:bg-emerald-200 rounded-xl mb-5 transition-colors">
                  <i className={`${s.icon} text-emerald-600 text-2xl`} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{s.desc}</p>
                <Link to="/services" className="text-emerald-600 font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all cursor-pointer whitespace-nowrap">
                  Learn More <i className="ri-arrow-right-line" />
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/services" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-10 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Teaser */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <span className="text-emerald-600 font-bold text-sm tracking-widest uppercase">Our Work</span>
            <h2 className="text-4xl font-extrabold text-gray-900 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Before &amp; After Results
            </h2>
            <p className="text-gray-500 mt-3 max-w-xl mx-auto">See the A&amp;S Prestige Cleaning Service difference — real homes and businesses transformed across Port St. Lucie and Martin County.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { img: 'https://readdy.ai/api/search-image?query=sparkling%20clean%20modern%20kitchen%20after%20deep%20cleaning%20polished%20countertops%20stainless%20appliances%20shiny%20floors%20bright%20south%20florida%20home%20beautifully%20staged&width=600&height=450&seq=port1&orientation=landscape', label: 'Kitchen Deep Clean – Port St. Lucie' },
              { img: 'https://readdy.ai/api/search-image?query=pristine%20clean%20luxury%20bathroom%20after%20professional%20cleaning%20shining%20tiles%20gleaming%20mirrors%20polished%20fixtures%20white%20fresh%20towels%20elegant%20south%20florida%20home&width=600&height=450&seq=port2&orientation=landscape', label: 'Bathroom Restoration – Stuart' },
              { img: 'https://readdy.ai/api/search-image?query=clean%20bright%20modern%20office%20space%20after%20commercial%20cleaning%20polished%20floors%20organized%20desks%20plants%20neat%20professional%20environment%20south%20florida&width=600&height=450&seq=port3&orientation=landscape', label: 'Commercial Office Clean – Jensen Beach' },
            ].map((p) => (
              <div key={p.label} className="rounded-2xl overflow-hidden group cursor-pointer">
                <div className="relative h-64 overflow-hidden">
                  <img src={p.img} alt={p.label} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="bg-white px-5 py-4">
                  <p className="text-gray-800 font-semibold text-sm">{p.label}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/portfolio" className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-600 hover:text-white font-bold px-10 py-4 rounded-full transition-colors whitespace-nowrap cursor-pointer inline-block">
              View Full Portfolio
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <span className="text-emerald-600 font-bold text-sm tracking-widest uppercase">Reviews</span>
            <h2 className="text-4xl font-extrabold text-gray-900 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              What Our Customers Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t) => (
              <div key={t.name} className="bg-gray-50 rounded-2xl p-8">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-base" />
                  ))}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="w-11 h-11 rounded-full object-cover object-top" />
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="relative py-24 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=aerial%20view%20beautiful%20residential%20neighborhood%20port%20st%20lucie%20florida%20sunny%20day%20green%20lawns%20clean%20streets%20suburban%20community%20warm%20lighting%20vibrant&width=1440&height=500&seq=cta1&orientation=landscape"
          alt="Serving Port St. Lucie and Martin County"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-emerald-900/80" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white mb-5" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Ready for a Cleaner Space?
          </h2>
          <p className="text-emerald-100 text-lg mb-10">
            Serving homes and businesses throughout Port St. Lucie and Martin County. Get your free quote today — no obligation, no hassle.
          </p>
          <Link to="/contact" className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold px-12 py-4 rounded-full transition-colors text-base whitespace-nowrap cursor-pointer inline-block">
            Request a Free Quote
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-bold text-sm tracking-widest uppercase">FAQ</span>
            <h2 className="text-4xl font-extrabold text-gray-900 mt-3" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Common Questions
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-semibold text-gray-900 text-sm pr-4">{faq.q}</span>
                  <div className="w-5 h-5 flex items-center justify-center shrink-0">
                    <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-emerald-600`} />
                  </div>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-gray-500 text-sm leading-relaxed">{faq.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
